import React, { useCallback, useState } from "react";
import { observer } from "mobx-react";
import { useParams } from "react-router-dom";
import { useStores } from "../../hooks/useStores";
import ProjectLoader from "./ProjectLoader";
import UnknownProject from "./UnknownProject";
import "./styles.css";
import ProjectVersion from "./ProjectVersion";
import ProjectActions from "./ProjectActions";
import ImageContext, { type CaptureOET } from "../ImageContext";
import { projectVersionLifecycleKey } from "./lifecycleKey";

/**
 * Top-level component for a project.
 *
 * This component renders:
 * - an error message if the project is not found.
 * - a loader component if the content for the active version is not yet
 *   available
 * - otherwise:
 *   - the ProjectVersion component for the active version
 *   - the ProjectActions component for the project
 *   - Both wrapped in an ImageContext provider (both have children that are
 *     consumers of the ImageContext)
 *
 * The `project` that used to be injected is derived here from the `:id` route
 * param (useParams) against the projects store (useStores). This replaces the
 * old `withRouter(inject(({ store: { projects } }, { match }) => ({ project:
 * projects.getProjectById(match.params.id) })))` wrapper. Looking up an unknown
 * id returns undefined, which keeps the UnknownProject fallback intact (see the
 * regression test in src/stores/projects/index.test.js).
 */
export default observer(function Project() {
  const store = useStores();
  // This route always carries an `:id` param, so it is a string at runtime; the
  // store lookup is typed `getProjectById(id: string)`. An unknown id resolves
  // to undefined, preserving the UnknownProject fallback below.
  const { id } = useParams();

  const project = store.projects.getProjectById(id!);

  // The captureOET callback is supplied by a descendant (the OET) and consumed
  // by ProjectActions via ImageContext. setState only ever set this single
  // field, so a plain useState matches the original merge semantics exactly.
  const version = project?.model?.activeVersion;
  const versionLifecycleKey = version ? projectVersionLifecycleKey(version) : null;
  const [captureOETState, setCaptureOETState] = useState<{
    key: string | null;
    fn: CaptureOET | null;
  }>({ key: null, fn: null });

  const captureOET =
    captureOETState.key === versionLifecycleKey ? captureOETState.fn : null;

  const handleSetCaptureOET = useCallback(
    (fn: CaptureOET) => {
      setCaptureOETState({ key: versionLifecycleKey, fn });
    },
    [versionLifecycleKey]
  );

  // Render error message when the project is missing.
  if (!project) {
    return <UnknownProject />;
  }

  // `project.model` and its `activeVersion` are non-null once a project is
  // present and active (the legacy code dereferenced both directly); assert to
  // keep the same flow.
  const activeVersion = version!;

  // Render loader if content of active version is not yet available.
  if (!activeVersion.contentAvailable) {
    return <ProjectLoader key={versionLifecycleKey} project={project} />;
  }

  // Render the project's active version and the project actions
  return (
    <ImageContext.Provider
      value={{
        captureOET: captureOET
          ? captureOET
          : () => Promise.reject("Not available"),
        setCaptureOET: handleSetCaptureOET
      }}
    >
      <ProjectVersion
        key={versionLifecycleKey}
        version={activeVersion}
        notation={project.model!.notation}
      />
      <ProjectActions project={project} />
    </ImageContext.Provider>
  );
});
